// @flow

import { COMMITTED_CAPACITY } from '@streaming-projects/user-commit-details/committed-capacity/enums';
import {
  areAllValuesInGivenObjectNonNull,
  getAllElementsOfArrayExceptLast,
  getValuesOfGivenKeysFromObjectAsArray,
  groupArrayOfObjectsByGivenKey,
} from '@src/common-utils/utils';
import { array, number, object, string } from 'yup';
import { MAX_INPUT_VALUE_FOR_ANY_FIELD } from '@src/constants';
import { doDimensionValuesConflict } from '@components/custom-discounts/utils';
import {
  getDimensionLevelConfigArrayFromMetricRow,
  getIfGivenRowIsANewlyAddedRow,
  getIfGivenRowIsBeingEditedORANewOne,
  getMetricDimensionsFromMetricName,
  getMinQuantityForMetricAndUserType,
} from '@streaming-projects/user-commit-details/committed-capacity/utils';
import { FORM_NAMES } from '@streaming-projects/enums';

const conflictsTest = (data, { createError, path, from }) => {
  const currRow = from[0].value;

  // Only check for conflicts on row addition
  if (!getIfGivenRowIsANewlyAddedRow(currRow)) {
    return true;
  }

  const parentInputs = from[1].value;
  const metricName = parentInputs[COMMITTED_CAPACITY.METRIC_NAME_BACKEND_FIELD_NAME];
  const allRows = parentInputs[COMMITTED_CAPACITY.METRICS_DIMENSIONS_LEVEL_CONFIGURATION_ARRAY];

  const dimRelatedKeys = getMetricDimensionsFromMetricName(metricName);
  const inProgressRowDimValues = getValuesOfGivenKeysFromObjectAsArray(currRow, dimRelatedKeys);

  let conflictingRowNum = null;

  // If there are more than 1 Committed Capacity Config items defined and all the values are filled
  if (allRows.length > 1 && areAllValuesInGivenObjectNonNull(currRow)) {
    // Then check if the last row value conflicts with any other row
    for (let rowNumber = 0; rowNumber <= allRows.length - 2; rowNumber++) {
      const existingRowDimValues = getValuesOfGivenKeysFromObjectAsArray(
        allRows[rowNumber],
        dimRelatedKeys
      );

      if (doDimensionValuesConflict(inProgressRowDimValues, existingRowDimValues)) {
        conflictingRowNum = rowNumber;
        break;
      }
    }
  }

  if (conflictingRowNum != null) {
    return createError({
      message: `This row conflicts with row ${conflictingRowNum + 1} , please check!`,
      path: path,
    });
  }

  return true;
};

const formatCommittedCapacityIntoFrontEndFormat = (committedCapacityInputs) => {
  // Step 1: Group the array by metric_type
  const groupedByMetric = groupArrayOfObjectsByGivenKey(committedCapacityInputs, 'metric_type');

  // Step 2: Transform each metric group
  return Object.entries(groupedByMetric).map(([metricType, items]) => ({
    metricName: metricType, // FE uses metricName everywhere which is more natural way of thinking about it
    [COMMITTED_CAPACITY.METRICS_DIMENSIONS_LEVEL_CONFIGURATION_ARRAY]: items.map((item) => ({
      [COMMITTED_CAPACITY.ID_BACKEND_FIELD_NAME]: item[COMMITTED_CAPACITY.ID_BACKEND_FIELD_NAME],
      [COMMITTED_CAPACITY.QUANTITY_BACKEND_FIELD_NAME]:
        item[COMMITTED_CAPACITY.QUANTITY_BACKEND_FIELD_NAME],
      ...item.resource_configuration_type,
    })),
  }));
};

export const committedCapacityValidationFunc = ({ isUserAdmin }) => {
  return array(
    object({
      [COMMITTED_CAPACITY.METRIC_NAME_BACKEND_FIELD_NAME]: string()
        .required()
        .label(COMMITTED_CAPACITY.METRIC_NAME_DISPLAY_NAME)
        .nullable()
        .typeError('Please select a metric!')
        .test(
          'onlyOneRowCanBeAddedAtATime',
          'Please ensure only 1 row is added at a time',
          function () {
            const dimensionLevelRows = getDimensionLevelConfigArrayFromMetricRow(this.parent);
            return getAllElementsOfArrayExceptLast(dimensionLevelRows).every(
              (dimensionConfigItem) => !getIfGivenRowIsANewlyAddedRow(dimensionConfigItem)
            );
          }
        )
        .test(
          'atLeastOneDimRowShouldBeDefined',
          'Please ensure at least 1 row is added inside a metric',
          function () {
            const dimensionLevelRows = getDimensionLevelConfigArrayFromMetricRow(this.parent);
            return dimensionLevelRows.length > 0;
          }
        ),
      [COMMITTED_CAPACITY.METRICS_DIMENSIONS_LEVEL_CONFIGURATION_ARRAY]: array(
        object({
          [COMMITTED_CAPACITY.QUANTITY_BACKEND_FIELD_NAME]: number()
            .integer()
            .nullable()
            .typeError('Quantity is a required field!')
            .label(COMMITTED_CAPACITY.QUANTITY_DISPLAY_NAME)
            .min(0)
            .max(MAX_INPUT_VALUE_FOR_ANY_FIELD)
            .required()
            .test('conflictsExist', 'Please ensure there are NO conflicts', conflictsTest)
            .test('dynamicMinQuantityCheck', (value, { createError, path, from }) => {
              const currRow = from[0].value;
              /*
              Only check min when the user is editing the row or configuring for the first time.Why?
              Think of this scenario:
              1. Admin User Configures Quantity as say 2 for ECKU. This is allowed for an admin but not for a non-admin.
              2. For a non-admin, this should not result in any error UNTIL he touches that field
            */
              if (!getIfGivenRowIsBeingEditedORANewOne(currRow)) {
                return true;
              }
              const parentInputs = from[1].value;
              const metricName = parentInputs[COMMITTED_CAPACITY.METRIC_NAME_BACKEND_FIELD_NAME];
              const minQuantity = getMinQuantityForMetricAndUserType(metricName, isUserAdmin);

              if (value < minQuantity) {
                return createError({
                  message: `Quantity should be greater than or equal to ${minQuantity}!`,
                  path: path,
                });
              }

              return true;
            }),
        })
      ).min(1),
    })
  );
};

export const COMMITTED_CAPACITY_JSON_CONFIG = {
  backendFieldName: COMMITTED_CAPACITY.METRICS_LEVEL_CONFIGURATION_ARRAY,
  inputTransformationFunc: formatCommittedCapacityIntoFrontEndFormat,
  validation: committedCapacityValidationFunc,
  belongingForm: FORM_NAMES.COMMITTED_CAPACITY.COMMITTED_CAPACITY_FORM,
};
